import axios from 'axios'
import router from '../router'

// 支持通过环境变量指定后端地址，或者使用相对路径走代理
const apiBaseUrl = (process.env.VUE_APP_API_BASE_URL || '').replace(/\/+$/, '')
// 如果有明确的 API 地址则直接使用，否则使用相对路径走 vue 代理
const coreBaseUrl = apiBaseUrl ? `${apiBaseUrl}/api/core` : '/api/core'
const profileBaseUrl = apiBaseUrl ? `${apiBaseUrl}/api` : '/api'

const coreApi = axios.create({
    baseURL: coreBaseUrl,
    timeout: 120000,
    withCredentials: true
})

const profileApi = axios.create({
    baseURL: profileBaseUrl,
    timeout: 120000,
    withCredentials: true
})

const legacyApi = axios.create({
    baseURL: apiBaseUrl || '',
    timeout: 120000,
    withCredentials: true
})

// 请求拦截器：自动添加Authorization头
coreApi.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('access_token')
        if (token) {
            config.headers.Authorization = `Bearer ${token}`
        }
        return config
    },
    (error) => {
        return Promise.reject(error)
    }
)

// profileApi 复用相同的拦截器
profileApi.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('access_token')
        if (token) {
            config.headers.Authorization = `Bearer ${token}`
        }
        return config
    },
    (error) => {
        return Promise.reject(error)
    }
)

legacyApi.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('access_token')
        if (token) {
            config.headers.Authorization = `Bearer ${token}`
        }
        return config
    },
    (error) => {
        return Promise.reject(error)
    }
)

// 响应拦截器：处理401错误
coreApi.interceptors.response.use(
    (response) => {
        return response
    },
    (error) => {
        if (error.response && error.response.status === 401) {
            // 清除本地存储的token
            localStorage.removeItem('access_token')
            localStorage.removeItem('refresh_token')
            localStorage.removeItem('user')
            
            // 跳转到登录页
            if (router.currentRoute.path !== '/login') {
                router.push('/login')
            }
        }
        return Promise.reject(error)
    }
)

// profileApi 复用相同的响应拦截器
profileApi.interceptors.response.use(
    (response) => {
        return response
    },
    (error) => {
        if (error.response && error.response.status === 401) {
            localStorage.removeItem('access_token')
            localStorage.removeItem('refresh_token')
            localStorage.removeItem('user')
            if (router.currentRoute.path !== '/login') {
                router.push('/login')
            }
        }
        return Promise.reject(error)
    }
)

legacyApi.interceptors.response.use(
    (response) => {
        return response
    },
    (error) => {
        if (error.response && error.response.status === 401) {
            localStorage.removeItem('access_token')
            localStorage.removeItem('refresh_token')
            localStorage.removeItem('user')
            if (router.currentRoute.path !== '/login') {
                router.push('/login')
            }
        }
        return Promise.reject(error)
    }
)

const getErrorMessage = (error, fallback = '操作失败') => {
    const data = error?.response?.data
    if (data?.error) return String(data.error)
    if (typeof data === 'string' && data.trim()) return data
    if (error?.message) return String(error.message)
    return fallback
}

const api = {
    getErrorMessage,
    getMeta() {
        return coreApi.get('/meta/')
    },
    // 部门相关
    getDepartments() {
        return coreApi.get('/departments/')
    },
    
    // 用户相关
    getUsers(params) {
        return coreApi.get('/users/', { params })
    },
    login(username, password) {
        return legacyApi.post('/api/profiles/login/', { username, password })
    },
    register(userData) {
        return legacyApi.post('/api/profiles/register/', userData)
    },
    changePassword(old_password, new_password, user_id) {
        const payload = { old_password, new_password }
        if (user_id !== undefined && user_id !== null && user_id !== '') payload.user_id = user_id
        return legacyApi.post('/api/profiles/change-password/', payload)
    },
    
    // 消息相关
    getMessages(userId) {
        return coreApi.get('/messages/', { params: { user_id: userId } })
    },
    createMessage(data) {
        return coreApi.post('/messages/', data)
    },
    updateMessage(id, data) {
        return coreApi.post('/messages/mark-read/', data)
    },
    markAllMessagesAsRead(messageIds) {
        const body = Array.isArray(messageIds) && messageIds.length > 0
            ? { message_ids: messageIds }
            : {}
        return coreApi.post('/messages/mark-read-batch/', body)
    },
    getMessageStats() {
        return coreApi.get('/messages/stats/')
    },
    
    // 任务相关
    getTasks(params) {
        return coreApi.get('/tasks/', { params })
    },
    getMyTasks() {
        return coreApi.get('/tasks/my/')
    },
    createTask(data) {
        return coreApi.post('/tasks/', data)
    },
    updateTask(id, data) {
        return coreApi.put(`/tasks/${id}/`, data)
    },
    
    // 审批相关
    getApprovals(params) {
        return coreApi.get('/approvals/', { params })
    },
    processApproval(id, data) {
        return coreApi.post('/approvals/process/', { id, ...data })
    },
    createApproval(data) {
        return coreApi.post('/approvals/create/', data)
    },
    
    // 文档相关
    getDocuments(params) {
        return coreApi.get('/documents/', { params })
    },
    createDocument(data) {
        return coreApi.post('/documents/', data)
    },
    deleteDocument(id) {
        return coreApi.delete(`/documents/${id}`)
    },
    getBackendBaseURL() {
        const base = coreApi.defaults.baseURL || '/api/core'
        return String(base).replace(/\/api\/core\/?$/, '') || ''
    },
    
    // 日历相关
    getEvents() {
        return coreApi.get('/events/')
    },
    
    // 设备相关（保留原有API）
    getEquipments(params) {
        return legacyApi.get('/api/equipments/', { params })
    },
    getEquipment(id) {
        return legacyApi.get(`/api/equipments/${id}/`)
    },
    getEquipmentStats() {
        return legacyApi.get('/api/equipments/stats/')
    },
    createEquipment(data) {
        return legacyApi.post('/api/equipments/', data)
    },
    updateEquipment(id, data) {
        return legacyApi.put(`/api/equipments/${id}/`, data)
    },
    deleteEquipment(id) {
        return legacyApi.delete(`/api/equipments/${id}/`)
    },
    reportFault(id, data) {
        return legacyApi.post(`/api/equipments/${id}/report_fault/`, data)
    },
    repairComplete(id) {
        return legacyApi.post(`/api/equipments/${id}/repair_complete/`, {})
    },
    
    // 设备分类（保留原有API）
    getCategories() {
        return legacyApi.get('/api/categories/')
    },
    createCategory(data) {
        return legacyApi.post('/api/categories/', data)
    },
    updateCategory(id, data) {
        return legacyApi.put(`/api/categories/${id}/`, data)
    },
    deleteCategory(id) {
        return legacyApi.delete(`/api/categories/${id}/`)
    },
    
    // 借用记录（保留原有API）
    getBorrows(params) {
        return legacyApi.get('/api/borrows/', { params })
    },
    createBorrow(data) {
        return legacyApi.post('/api/borrows/', data)
    },
    returnEquipment(id, data) {
        return legacyApi.post(`/api/borrows/${id}/return_equipment/`, data)
    },

    // 用户资料
    getProfiles(params) {
        return legacyApi.get('/api/profiles/', { params })
    },
    getProfile(id) {
        return legacyApi.get(`/api/profiles/${id}/`)
    },
    createProfile(data) {
        return legacyApi.post('/api/profiles/', data)
    },
    updateProfile(id, data) {
        return legacyApi.patch(`/api/profiles/${id}/`, data)
    },
    deleteProfile(id) {
        return profileApi.delete(`/${id}`)
    },
    
    // OpenClaw相关（直接调用笔记本上的服务）
    getOpenClawStatus() {
        const baseUrl = process.env.VUE_APP_OPENCLAW_API_BASE_URL || 'http://100.90.7.105:3000'
        return axios.get(`${baseUrl}/api/status/`)
    },
    triggerOpenClaw(action) {
        const baseUrl = process.env.VUE_APP_OPENCLAW_API_BASE_URL || 'http://100.90.7.105:3000'
        return axios.post(`${baseUrl}/api/trigger/`, { action })
    },
    
    // 审计日志（Go：需登录 + 管理员，走 coreApi 带 Token）
    getAuditLogs() {
        return coreApi.get('/audit-logs/')
    },
    
    // AI助手
    aiHealth() {
        return coreApi.get('/ai/health/')
    },
    aiChat(question, userId, sessionId, extra = {}) {
        return coreApi.post('/ai/chat/', { question, user_id: userId, session_id: sessionId, ...extra })
    },
    aiHistory(userId, sessionId) {
        const params = {}
        if (sessionId) params.session_id = sessionId
        return coreApi.get(`/ai/history/${userId}`, { params })
    },
    aiClear(userId, sessionId) {
        return coreApi.post('/ai/clear/', { user_id: userId, session_id: sessionId })
    },

    // 低代码元数据管理（与 go-zero routes：/api/core/lowcode/admin 一致）
    createLowCodeApp(data) {
        return coreApi.post('/lowcode/admin/apps/', data)
    },
    createLowCodeForm(data) {
        return coreApi.post('/lowcode/admin/forms/', data)
    },
    createLowCodeField(data) {
        return coreApi.post('/lowcode/admin/fields/', data)
    },
    createLowCodeFlow(data) {
        return coreApi.post('/lowcode/admin/flows/', data)
    },
    createLowCodeRule(data) {
        return coreApi.post('/lowcode/admin/rules/', data)
    },
    createLowCodeFieldOption(data) {
        return coreApi.post('/lowcode/admin/field-options/', data)
    },
    getLowCodeFieldOptions(fieldId) {
        return coreApi.get('/lowcode/field-options/', { params: { field_id: fieldId } })
    },
    getLowCodeApps() {
        return coreApi.get('/lowcode/apps/')
    },
    deleteLowCodeApp(id) {
        return coreApi.delete(`/lowcode/admin/apps/${id}`)
    },
    updateLowCodeApp(id, data) {
        return coreApi.put(`/lowcode/admin/apps/${id}`, data)
    },
    getLowCodeAppDetail(code) {
        return coreApi.get(`/lowcode/apps/?code=${code}`)
    },
    getLowCodeInstances(appCode, userId, status) {
        const params = { app_code: appCode }
        if (userId !== undefined && userId !== null && userId !== '') params.user_id = userId
        if (status !== undefined && status !== null && status !== '') params.status = status
        return coreApi.get('/lowcode/instances/', { params })
    },
    createLowCodeInstance(data) {
        return coreApi.post('/lowcode/instances/create/', data)
    },
    returnLowCodeInstance(data) {
        return coreApi.post('/lowcode/instances/return_equipment/', data)
    }
}

export default api
